import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import { ensureFileNameHasExtension } from "@/lib/utils/matterFile";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";
import { TemplateFieldsDialog } from "@/components/features/settings/defaults/matter-templates/TemplateFieldsDialog";
import {
  DocxEditor,
  DOCX_MIME_TYPE,
  useDocxTemplateFieldActions,
  type DocxEditorFileType,
  type DocxEditorHandle,
} from "./DocxEditor";

const createDocxSchema = z.object({
  fileName: z.string().trim().min(1, "File name is required"),
});

const normalizeDocxFileName = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return ensureFileNameHasExtension(trimmed, "docx");
};

interface CreateDocxSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  description: string;
  fileType: DocxEditorFileType;
  onSave: (file: File, normalizedFileName: string) => Promise<void> | void;
  initialFileName?: string;
  existingFileNames?: string[];
  editorFileName?: string;
}

export const CreateDocxSlideover = ({
  open,
  setOpen,
  title,
  description,
  fileType,
  onSave,
  initialFileName = "New document",
  existingFileNames = [],
  editorFileName = "New document.docx",
}: CreateDocxSlideoverProps) => {
  const editorRef = useRef<DocxEditorHandle | null>(null);
  const {
    templateFieldsTriggerRef,
    handleInsertAtCursor,
    handleOpenTemplateFields,
  } = useDocxTemplateFieldActions(editorRef);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const form = useForm<z.infer<typeof createDocxSchema>>({
    resolver: zodResolver(createDocxSchema),
    defaultValues: {
      fileName: initialFileName,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      form.reset({ fileName: initialFileName });
      setIsEditorReady(false);
      setIsExporting(false);
      return;
    }
    form.reset({ fileName: initialFileName });
  }, [form, initialFileName, open]);

  const handleSave = form.handleSubmit(async (data) => {
    if (!editorRef.current) {
      toast.error("Document editor is not ready yet.");
      return;
    }

    const normalizedFileName = normalizeDocxFileName(data.fileName);
    if (!normalizedFileName) {
      form.setError("fileName", {
        type: "manual",
        message: "File name is required",
      });
      return;
    }

    const existingNames = new Set(
      existingFileNames.map((name) => name.toLowerCase()),
    );
    if (existingNames.has(normalizedFileName.toLowerCase())) {
      form.setError("fileName", {
        type: "manual",
        message: "A file with this name already exists",
      });
      return;
    }

    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      const file = new File([blob], normalizedFileName, {
        type: DOCX_MIME_TYPE,
      });

      await onSave(file, normalizedFileName);
      setOpen(false);
    } catch (error) {
      console.error("Failed to create document", error);
      toast.error("Failed to create document");
    } finally {
      setIsExporting(false);
    }
  });

  const isSaving = isExporting;
  const formId = "create-docx-form";

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      isDirty={form.formState.isDirty}
      bodyContained
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      className="top-0 left-0 z-[109] h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
      footer={
        <div className="flex w-full justify-end gap-x-2">
          <FormCancelButton
            setOpen={setOpen}
            isDirty={form.formState.isDirty}
          />
          <Button
            type="submit"
            form={formId}
            variant="light-primary"
            disabled={!isEditorReady || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      }
    >
      <Form {...form}>
        <form
          id={formId}
          className="relative flex h-full min-h-0 grow flex-col overflow-hidden"
          onSubmit={handleSave}
        >
          {isSaving && <FormLoadingOverlay />}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 py-4">
              <div className="flex flex-wrap items-end gap-3">
                <FormField
                  control={form.control}
                  name="fileName"
                  render={({ field }) => (
                    <FormItem className="max-w-sm flex-1">
                      <div className="flex">
                        <FormLabel>File name</FormLabel>
                        <FormMessage
                          data-testid="error-message"
                          className="grow text-right"
                        />
                      </div>
                      <FormControl>
                        <div className="flex items-center">
                          <Input placeholder="New document" {...field} />
                          <span className="text-muted-foreground ml-2 text-sm">
                            .docx
                          </span>
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <TemplateFieldsDialog
                  hideTrigger
                  onInsertAtCursor={handleInsertAtCursor}
                  triggerLabel="Template variables"
                  triggerRef={templateFieldsTriggerRef}
                />
              </div>
              <DocxEditor
                ref={editorRef}
                fileName={editorFileName}
                presignedUrlKey=""
                fileType={fileType}
                licenseKey={VITE_APRYSE_LICENSE_KEY}
                loadBlankDocument
                onTemplateFieldsRequest={handleOpenTemplateFields}
                onReadyChange={setIsEditorReady}
                isActive={open}
                showSaveButton={false}
                showHeader={false}
                className="mt-2 min-h-0 flex-1"
              />
            </div>
          </div>
        </form>
      </Form>
    </AppModal>
  );
};
